import {inject, Injectable, Injector, PLATFORM_ID} from '@angular/core';
import {GtmEnum} from '../models/gtm.enum';
import {v4 as uuidv4} from 'uuid';
import {OrderTrackingEntry} from '../../data/models/order-tracking-entry.model';
import {HttpClient} from '@angular/common/http';
import {DatePipe, isPlatformBrowser} from '@angular/common';
import {LoginService} from '../components/login/login.service';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';

interface GtmDataLayer {
  push: (entry: object) => unknown;
}

function isGtmDataLayer(value: unknown): value is GtmDataLayer {
  return typeof value === 'object' && value !== null && 'push' in value && typeof value.push === 'function';
}

@Injectable({providedIn: 'root'})
export class OrderTrackingService {
    private readonly http = inject(HttpClient);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly datePipe = inject(DatePipe);
    private readonly injector = inject(Injector);
    private loginService: LoginService;

    transactionId : string;

    pushEvent(event: GtmEnum, eventData: object): void {

      // ueber globalThis lesen: ein nicht definiertes globales dataLayer (kein GTM eingebunden) wuerde
      // als nackter Bezeichner einen ReferenceError werfen; in const fangen, damit das Narrowing
      // im subscribe-Callback erhalten bleibt
      const dataLayerCandidate = isPlatformBrowser(this.platformId) ? globalThis.dataLayer : undefined;
      const gtmDataLayer = isGtmDataLayer(dataLayerCandidate) ? dataLayerCandidate : null;
      if (gtmDataLayer) {
          const filteredData: any = {...eventData['body'] ?? eventData};
          delete filteredData.registerPassword;
          delete filteredData.deliveryAddress;
          delete filteredData.billingAddress;
          delete filteredData.bonusAddress;
          delete filteredData.payment;
          delete filteredData.billingAddressList;
          delete filteredData.deliveryAddressList;
          delete filteredData.paymentList;
          delete filteredData.email;
          delete filteredData.selectedGiftList;
          delete filteredData.additionalData;

          // P5W-4011 (add userSSOId to GTM)
          if (!this.loginService) {
            // due to a circular dependency, we need to get the service from the injector rather than as attribute injection
            this.loginService = this.injector.get(LoginService);
          }

          // Der Push muss warten, bis die userSSOId feststeht: getUserData() antwortet nur
          // dann synchron, wenn die Nutzerdaten schon gecacht sind. Beim ersten Event nach
          // dem Laden lief der Push sonst vor der Antwort und die userSSOId fehlte.
          // resolveUserSsoId() liefert fuer anonyme Nutzer und im Fehlerfall synchron null,
          // der Push findet also in jedem Fall statt.
          this.resolveUserSsoId().subscribe(userSSOId => {
            if (userSSOId) {
              filteredData.userSSOId = userSSOId;
            }
            gtmDataLayer.push({event, objectData: JSON.stringify(filteredData)});
          });
        }

        if(event !== GtmEnum.SITE_NAVIGATION) {
          let eventDataList = [];
          const filteredData: any = {...eventData['body'] ?? eventData};
          if(!Array.isArray(filteredData)) {
            delete filteredData.registerPassword;
            eventDataList.push(filteredData);
          } else {
            filteredData.forEach(o => delete o.registerPassword)
            eventDataList = filteredData;
          }
          if (isPlatformBrowser(this.platformId)) {
            try {
              let orderStep = new OrderTrackingEntry();

              orderStep.ts = this.datePipe.transform(Date(), 'yyyy-MM-dd HH:mm:ss');
              orderStep.transactionId = this.transactionId;
              orderStep.eventType = event?.toString()?.toUpperCase();
              orderStep.eventData = eventDataList;

              this.http.post('webabo/tracking/orderevent', orderStep).subscribe(success => success);
            } catch (e) {
              console.error("Error while saving tracking: " + e);
            }
          }
        }
    }

    /**
     * Liefert die userSSOId des angemeldeten Nutzers, sonst null (anonym, keine SSO-Id, Ladefehler).
     * Fuer anonyme Nutzer wird bewusst kein GET /webabo/users abgesetzt - die Bestellstrecke
     * laeuft auch ohne Anmeldung, dort wuerde der Aufruf nur fehlschlagen.
     */
    private resolveUserSsoId(): Observable<number | null> {
      return this.loginService.getAuthData().pipe(
        take(1),
        switchMap(authData => {
          if (!authData?.token) {
            return of<number | null>(null);
          }
          return this.loginService.getUserData().pipe(
            take(1),
            map(userData => userData?.userSSOId ?? null),
            catchError(error => {
              console.error('Nutzerdaten fuer das GTM-Tracking konnten nicht geladen werden: ', error);
              return of<number | null>(null);
            })
          );
        })
      );
    }

    generateTransactionId(): void {
      this.transactionId = uuidv4();
    }

    getTransactionId(): string {
      return this.transactionId;
    }
}
